// src/pages/DoctorsJobsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import {  FaPaperPlane, FaTag, FaLock } from 'react-icons/fa';
import { IoBagHandleOutline, IoLocationOutline, IoStorefrontOutline } from "react-icons/io5";
import { IoBookmarkOutline, IoBookmark } from "react-icons/io5";
import { useAuth } from '../contexts/AuthContext';
import JobApplicationsTracker from '../components/home/JobApplicationsTracker';
import InterviewPracticeSection from '../components/home/InterviewPracticeSection';
import CVTracker from '../components/home/CVTracker';
import JobBoardSection from '../components/home/JobBoardSection';
import FAQSection from '../components/home/FAQSection';

const DoctorsJobsPage = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.substring(1); // Remove the # symbol
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 80; // Approximate navbar height
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const [availableJobTypes, setAvailableJobTypes] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [displayedJobs, setDisplayedJobs] = useState([]);
  const [bookmarkedJobs, setBookmarkedJobs] = useState([]);

  // Fetch jobs from Supabase
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        
        // Get all jobs from Supabase
        const { data, error: supabaseError } = await supabase
          .from('jobs')
          .select('*');
          
        if (supabaseError) throw supabaseError;
        
        // Sort the jobs by id or date added if available
        const sortedData = data ? [...data].sort((a, b) => {
          // First try to sort by date_added if available
          if (a.date_added && b.date_added) {
            return new Date(b.date_added) - new Date(a.date_added); // newest first
          }
          // Fall back to id sorting
          return a.id - b.id;
        }) : [];
        setJobs(sortedData);
        
        // If user is not logged in, only the first 6 jobs will be displayed
        if (!user) {
          setFilteredJobs(sortedData);
        } else {
          setFilteredJobs(sortedData);
        }
        
        // Extract unique categories, locations, provinces, job types, and tags
        if (sortedData && sortedData.length > 0) {
          const categories = [...new Set(sortedData.map(job => job.category))].filter(Boolean);
          const locations = [...new Set(sortedData.map(job => job.continent))].filter(Boolean);
          const provinces = [...new Set(sortedData.map(job => job.province))].filter(Boolean);
          const jobTypes = [...new Set(sortedData.map(job => job.fulltime_parttime))].filter(Boolean);
          
          // Extract tags - if tags are comma-separated in a single field
          const allTags = [];
          sortedData.forEach(job => {
            if (job.tags) {
              const jobTags = job.tags.split(',').map(tag => tag.trim());
              allTags.push(...jobTags);
            }
          });
          const uniqueTags = [...new Set(allTags)].filter(Boolean);
          
          setAvailableCategories(categories.sort());
          setAvailableLocations(locations.sort());
          setAvailableProvinces(provinces.sort());
          setAvailableJobTypes(jobTypes.sort());
          setAvailableTags(uniqueTags.sort());
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError('Failed to load jobs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [user]);

  // Helper function to check if deadline has passed
  const isDeadlineValid = (deadlineString) => {
    if (!deadlineString) return true; // If no deadline, show the job
    
    // If deadline is "see website" or similar non-date text, show the job
    if (deadlineString.toLowerCase().includes('see website') || 
        deadlineString.toLowerCase().includes('website') ||
        deadlineString.toLowerCase().includes('apply') ||
        !deadlineString.match(/\d/)) {
      return true;
    }
    
    try {
      // Parse date format like "10 Jun 25" or "22 May 25"
      const dateMatch = deadlineString.match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{2})/);
      if (!dateMatch) return true; // If we can't parse it, show the job
      
      const [, day, monthStr, year] = dateMatch;
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 
                         'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const monthIndex = monthNames.indexOf(monthStr.toLowerCase());
      
      if (monthIndex === -1) return true; // If invalid month, show the job
      
      // Assume years 00-30 are 2000-2030, 31-99 are 1931-1999
      const fullYear = parseInt(year) <= 30 ? 2000 + parseInt(year) : 1900 + parseInt(year);
      
      const deadlineDate = new Date(fullYear, monthIndex, parseInt(day));
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
      
      return deadlineDate >= today;
    } catch (error) {
      console.warn('Error parsing deadline:', deadlineString, error);
      return true; // If there's an error parsing, show the job
    }
  };

  // Filter and sort jobs based on all filters
  useEffect(() => {
    let result = jobs;
    
    // First, filter out jobs with passed deadlines
    result = result.filter(job => isDeadlineValid(job.deadline));
    
    // If user is not logged in, we still filter the jobs to show accurate total count
    // but we'll limit display to 6 in another useEffect
    if (!user) {
      setFilteredJobs(result);
      return;
    }
    
    // Apply category filter
    if (categoryFilter) {
      result = result.filter(job => job.category === categoryFilter);
    }
    
    // Apply location filter
    if (locationFilter) {
      result = result.filter(job => job.continent === locationFilter);
    }
    
    // Apply province filter
    if (provinceFilter) {
      result = result.filter(job => job.province === provinceFilter);
    }
    
    // Apply job type filter
    if (jobTypeFilter) {
      result = result.filter(job => job.fulltime_parttime === jobTypeFilter);
    }
    
    // Apply tag filter
    if (tagFilter && tagFilter !== '') {
      result = result.filter(job => 
        job.tags && job.tags.toLowerCase().includes(tagFilter.toLowerCase())
      );
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(job => 
        (job.title && job.title.toLowerCase().includes(query)) ||
        (job.company && job.company.toLowerCase().includes(query)) ||
        (job.location && job.location.toLowerCase().includes(query)) ||
        (job.tags && job.tags.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    if (sortOrder !== 'default') {
      result = [...result].sort((a, b) => {
        if (sortOrder === 'linkedin-yes') {
          // Show LinkedIn jobs first
          if (a.linkedin && !b.linkedin) return -1;
          if (!a.linkedin && b.linkedin) return 1;
          // If both have same LinkedIn status, sort by date
          const dateA = a.date_added ? new Date(a.date_added) : null;
          const dateB = b.date_added ? new Date(b.date_added) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        } else if (sortOrder === 'linkedin-no') {
          // Show non-LinkedIn jobs first
          if (!a.linkedin && b.linkedin) return -1;
          if (a.linkedin && !b.linkedin) return 1;
          // If both have same LinkedIn status, sort by date
          const dateA = a.date_added ? new Date(a.date_added) : null;
          const dateB = b.date_added ? new Date(b.date_added) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        } else {
          // Handle date-based sorting
          const dateA = a.date_added ? new Date(a.date_added) : null;
          const dateB = b.date_added ? new Date(b.date_added) : null;
          
          // Handle cases where one or both dates are missing
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          // Sort based on the selected order
          if (sortOrder === 'newest') {
            return dateB - dateA; // Newest first
          } else if (sortOrder === 'oldest') {
            return dateA - dateB; // Oldest first
          }
        }
        return 0;
      });
    }
    
    setFilteredJobs(result);
  }, [categoryFilter, locationFilter, provinceFilter, jobTypeFilter, tagFilter, searchQuery, sortOrder, jobs, user]);

  // Prepare the displayed jobs
  useEffect(() => {
    if (!user) {
      // For non-logged in users, limit to 6 jobs (5 fully visible + 1 blurred)
      setDisplayedJobs(jobs.slice(0, 5));
    } else {
      // For logged in users, show all filtered jobs
      setDisplayedJobs(filteredJobs);
    }
  }, [filteredJobs, jobs, user]);

  useEffect(() => {
    const fetchBookmarkedJobs = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('my_applications')
          .select('job_id')
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        setBookmarkedJobs(data.map(item => item.job_id));
      } catch (error) {
        console.error('Error fetching bookmarked jobs:', error);
      }
    };

    fetchBookmarkedJobs();
  }, [user]);

  const handleCategoryClick = (category) => {
    if (!user) {
      // If user is not logged in, show a message that they need to log in
      return;
    }
    
    if (categoryFilter === category) {
      // If already selected, clear the filter
      setCategoryFilter('');
    } else {
      // Set new filter
      setCategoryFilter(category);
    }
  };

  const clearAllFilters = () => {
    if (!user) return;
    
    setCategoryFilter('');
    setLocationFilter('');
    setProvinceFilter('');
    setJobTypeFilter('');
    setTagFilter('');
    setSearchQuery('');
    setSortOrder('default');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Only process search if logged in
    if (!user) return;
    // The filtering will be handled by the useEffect above
  };
  
  // Find the most recent date_added to display
  const findMostRecentUpdate = (jobs) => {
    if (!jobs || jobs.length === 0) return null;
    
    let mostRecent = null;
    
    jobs.forEach(job => {
      if (job.date_added) {
        const jobDate = new Date(job.date_added);
        if (!mostRecent || jobDate > mostRecent) {
          mostRecent = jobDate;
        }
      }
    });
    
    return mostRecent ? formatDate(mostRecent.toISOString()) : null;
  };

  // Helper function to get category tag class
  const getCategoryClass = (category) => {
    if (!category) return '';
    
    // Normalize the category name for consistent CSS class handling
    // Remove spaces, slashes, convert to lowercase
    let normalizedCategory = category.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/\//g, '-')
      .replace(/[^a-z0-9-]/g, '');
    
    // Handle special cases based on your data
    if (normalizedCategory.includes('clinical-medicine')) return 'clinical-medicine';
    if (normalizedCategory.includes('medical-education')) return 'medical-education';
    if (normalizedCategory.includes('medical-communication')) return 'medical-communication';
    if (normalizedCategory.includes('project-management')) return 'project-management';
    if (normalizedCategory.includes('pharmaceuticals') || normalizedCategory.includes('pharmaceutical')) return 'pharmaceuticals';
    if (normalizedCategory.includes('clinical-research') || normalizedCategory.includes('clinical-reserach')) return 'clinical-research';
    if (normalizedCategory.includes('healthtech') || normalizedCategory.includes('health-tech')) return 'health-tech';
    if (normalizedCategory.includes('hospital') || normalizedCategory.includes('clinic-management')) return 'hospital-clinic-management';
    if (normalizedCategory.includes('consulting') || normalizedCategory === 'management') return 'management-consulting';
    if (normalizedCategory.includes('entrepreneurship')) return 'entrepreneurship';
    if (normalizedCategory.includes('policy') || normalizedCategory.includes('legal') || normalizedCategory.includes('ethics')) return 'policy';
    if (normalizedCategory.includes('marketing') || normalizedCategory.includes('sales')) return 'marketing';
    if (normalizedCategory.includes('creative')) return 'creative';
    if (normalizedCategory.includes('wellness') || normalizedCategory.includes('lifestyle')) return 'wellness';
    if (normalizedCategory.includes('insurance')) return 'insurance';
      
    // Default fallback - return the first part of the category
    return normalizedCategory.split('-')[0];
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Check if it's already in the format we want (e.g., "15 Apr 25")
      if (/^\d{1,2}\s[A-Za-z]{3}\s\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Otherwise, format the date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // If invalid date, return original
      
      const day = date.getDate();
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
      const year = date.getFullYear().toString().substr(-2);
      
      return `${day} ${month} ${year}`;
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const handleBookmark = async (jobId) => {
    if (!user) return;

    try {
      const isBookmarked = bookmarkedJobs.includes(jobId);
      
      if (isBookmarked) {
        const { error } = await supabase
          .from('my_applications')
          .delete()
          .match({ user_id: user.id, job_id: jobId });

        if (error) throw error;
        
        setBookmarkedJobs(prev => prev.filter(id => id !== jobId));
      } else {
        const { error } = await supabase
          .from('my_applications')
          .insert([
            { 
              user_id: user.id, 
              job_id: jobId,
              status: 'bookmarked',
              created_at: new Date().toISOString()
            }
          ]);

        if (error) throw error;
        
        setBookmarkedJobs(prev => [...prev, jobId]);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (error) {
    return <div className="container">{error}</div>;
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* <Navbar /> */}
      <header className="header">
        <div className="header-content">
          {/* <div className="logo-container">
            <div className="logo">
              Doctors' Jobs <span className="logo-badge">Beta</span>
            </div>
          </div> */}
          
          <div className="slogan">
            <h1 className='font-medium'>Empowering Healthcare Careers.</h1>
            <h1 className='font-medium'>One opportunity at a time.</h1>
            <p className='text-[22px] mt-4'>Find better roles, optimise your CV, and stay organised, all in one powerful platform.</p>
          </div>
          
          <form onSubmit={handleSearch}>
            <div className={`search-container ${!user ? 'opacity-70 cursor-pointer' : ''}`} onClick={!user ? () => window.location.href = '/login' : undefined}>
              <div className="search-input">
                <IoLocationOutline />
                <select 
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  aria-label="Filter by location"
                  disabled={!user}
                >
                  <option value="">Country/Region</option>
                  {availableLocations.map((location, index) => (
                    <option key={index} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              
              <div className="search-input">
                <IoLocationOutline />
                <select 
                  value={provinceFilter}
                  onChange={(e) => setProvinceFilter(e.target.value)}
                  aria-label="Filter by province"
                  disabled={!user}
                >
                  <option value="">Provinces</option>
                  {availableProvinces.map((province, index) => (
                    <option key={index} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              
              <div className="search-input">
                <IoBagHandleOutline />
                <select 
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                  disabled={!user}
                >
                  <option value="">Category</option>
                  {availableCategories.map((category, index) => (
                    <option key={index} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="search-input">
                <IoStorefrontOutline />
                <select 
                  value={jobTypeFilter}
                  onChange={(e) => setJobTypeFilter(e.target.value)}
                  aria-label="Filter by job type"
                  disabled={!user}
                >
                  <option value="">Job Types</option>
                  {availableJobTypes.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              {availableTags.length > 0 && (
                <div className="search-input">
                  <FaTag />
                  <select 
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    aria-label="Filter by tag"
                    disabled={!user}
                  >
                    <option value="">All Tags</option>
                    {availableTags.map((tag, index) => (
                      <option key={index} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <button className="search-button" type="submit">
                Search
              </button>
            </div>
            {!user && (
              <div className="mt-2 text-center text-sm text-blue-600">
                <FaLock className="inline-block mr-1" /> 
                <Link to="/login" className="font-medium hover:underline">
                  Log in to unlock full search and filtering capabilities
                </Link>
              </div>
            )}
          </form>
        </div>
        
        <div className="paper-planes">
          <div 
            className="paper-plane"
            style={{
              top: '15%',
              right: '15%',
              transform: 'rotate(45deg) scale(1)'
            }}
          >
            <FaPaperPlane size={68} color="#ddd" />
          </div>
          <div 
            className="paper-plane"
            style={{
              top: '35%',
              right: '15%',
              transform: 'rotate(40deg) scale(1)'
            }}
          >
            <FaPaperPlane size={60} color="#ddd" />
          </div>
          <div 
            className="paper-plane"
            style={{
              top: '25%',
              right: '29%',
              transform: 'rotate(35deg) scale(1)'
            }}
          >
            <FaPaperPlane size={52} color="#ddd" />
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="content-container">
          <h2 className="section-title">Browse by category</h2>
          
          <div className={`category-filters ${!user ? 'opacity-70 cursor-pointer' : ''}`} onClick={!user ? () => window.location.href = '/login' : undefined}>
            {availableCategories.map((category, index) => (
              <button 
                key={index}
                className={`category-pill ${getCategoryClass(category)} ${categoryFilter === category ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
                disabled={!user}
              >
                {category}
              </button>
            ))}
            {user && (categoryFilter || locationFilter || provinceFilter || jobTypeFilter || tagFilter || searchQuery || sortOrder !== 'default') && (
              <button 
                className="category-pill clear-all"
                onClick={clearAllFilters}
              >
                Clear All
              </button>
            )}
          </div>
          
          {!user && (
            <div className="mt-2 mb-4 text-center text-sm text-blue-600">
              <FaLock className="inline-block mr-1" /> 
              <Link to="/login" className="font-medium hover:underline">
                Sign in to use category filters
              </Link>
            </div>
          )}
          
          <div className="results-header">
            <div className="results-and-order">
              <h2 className="results-count">
                Results ({!user ? `Showing 5 of ${filteredJobs.length}` : filteredJobs.length})
              </h2>
              {findMostRecentUpdate(jobs) && (
                <div className="last-updated-info">
                  Last updated: {findMostRecentUpdate(jobs)}
                </div>
              )}
            </div>
            <div className={`order-controls ${!user ? 'opacity-70 pointer-events-none' : ''}`}>
              <label htmlFor="sort-order">Order by: </label>
              <select 
                id="sort-order"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="sort-selector"
                disabled={!user}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#333',
                  fontSize: '15px',
                  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                }}
              >
                <option value="default">Default</option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="linkedin-yes">LinkedIn Posts</option>
                <option value="linkedin-no">Non-LinkedIn Posts</option>
              </select>
            </div>
          </div>
          
          <table className="jobs-table">
            <thead>
              <tr>
                <th style={{ display: !user && "none" }}>Pin</th>
                <th>Company</th>
                <th>Location</th>
                <th>Title</th>
                <th>Type</th>
                <th>Experience</th>
                <th>Category</th>
                <th>Application</th>
              </tr>
            </thead>
            <tbody>
              {displayedJobs.map((job, index) => (
                <tr 
                  key={job.id} 
                  className={`${!user && index === 5 ? "relative blur-none" : ""} ${bookmarkedJobs.includes(job.id) ? "bg-gray-50" : ""}`}
                >
                  <td data-label="Pin" style={{ display: !user && "none" }}>
                    <button 
                      onClick={() => handleBookmark(job.id)}
                      className="text-gray-400 hover:text-black transition-colors"
                    >
                      {bookmarkedJobs.includes(job.id) ? (
                        <IoBookmark className="text-black" />
                      ) : (
                        <IoBookmarkOutline />
                      )}
                    </button>
                  </td>
                  <td data-label="Company">{job.company}</td>
                  <td data-label="Location">{job.location}</td>
                  <td data-label="Title">{job.title}</td>
                  <td data-label="Type">{job.fulltime_parttime}</td>
                  <td data-label="Experience">{job.experience}</td>
                  <td data-label="Category">
                    <div className={`job-category-tag ${getCategoryClass(job.category)}`}>
                      {job.category}
                    </div>
                  </td>
                  <td data-label="Application">
                    {job.deadline && (
                      <div className="deadline">{formatDate(job.deadline)}</div>
                    )}
                    <a href={job.application_url} target="_blank" rel="noopener noreferrer">
                      <button className="apply-button">View Job</button>
                    </a>
                  </td>
                  
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Show a CTA button if not logged in and there are more than 6 jobs */}
          {!user && filteredJobs.length > 5 && (
            <div className="mt-8 text-center">
              <p className="text-lg text-gray-700 mb-4">
                <span className="font-medium">{filteredJobs.length - 5}</span> more jobs available
              </p>
              <Link 
                to="/login" 
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-full transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              >
                Sign in to see all {filteredJobs.length} jobs
              </Link>
            </div>
          )}
          
          <div className="disclaimer-text">
            *These jobs are posted on LinkedIn, which charges daily to advertise. We recommend applying quickly, as posts may open and close intermittently during the review process.
          </div>
        </div>
      </main>

      {!user && (
        <div className="mt-8">
          <JobApplicationsTracker />
          <InterviewPracticeSection />
          <CVTracker />
          <JobBoardSection />
          <FAQSection/>
        </div>
      )}
      
      {/* <footer className="footer">
        <div className="footer-links">
          <a href="https://www.fourminutemedicine.com/files/terms-and-conditions.pdf" target="_blank" rel="noopener noreferrer">Disclaimer</a> | 
          <a href="https://www.fourminutemedicine.com/files/privacy-policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a> | 
          <a href="https://www.fourminutemedicine.com/files/terms-of-service.pdf" target="_blank" rel="noopener noreferrer">Terms of Service</a> | 
          <a href="https://www.fourminutemedicine.com/contact-us" target="_blank" rel="noopener noreferrer">Contact Us</a>
        </div>
        <div className="footer-copyright">
          Copyright © 2025 Four Minute Medicine (PTY) LTD. All rights reserved.
        </div>
      </footer> */}
    </div>
  );
};

export default DoctorsJobsPage;